import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, Download, Plus } from 'lucide-react';
import Swal from 'sweetalert2';
import { EditActionBar } from '../shared/EditActionBar';
import type {
  Driver02Data,
  QuotaABranchRow,
  QuotaOptionalBranchRow,
  QuotaASection,
  QuotaOptionalSection,
} from '../../../api/apiTypesConductRisk';
import { useConductRiskDriver } from '../../../api/hooks/useConductRiskDriver';
import { fetchExportConductRiskDriverXlsx } from '../../../api/functionApi';
import { formatHighlights } from '../../../utils/Format';

/* ─── Tabs dinamiche ─────────────────────────────────────────
 * Sotto Quota A ci sono `regional` + `national`.
 * Per ogni quota opzionale (B, C, ...) presente in data.optional_quotas
 * si aggiungono altre due tab. */

type TabScope = 'regional' | 'national';

interface OutputTab {
  label:      string;
  quotaKey:   'A' | string;   // 'A' oppure la chiave della quota opzionale ('B', 'C', ...)
  scope:      TabScope;
  isQuotaA:   boolean;
}

const buildOutputTabs = (data: Driver02Data | null): OutputTab[] => {
  const tabs: OutputTab[] = [
    { label: 'Quota A — Per DR',    quotaKey: 'A', scope: 'regional', isQuotaA: true },
    { label: 'Quota A — Nazionale', quotaKey: 'A', scope: 'national', isQuotaA: true },
  ];
  if (data) {
    const optionalKeys = Object.keys(data.optional_quotas ?? {}).sort();
    for (const k of optionalKeys) {
      tabs.push({ label: `Quota ${k} — Per DR`,    quotaKey: k, scope: 'regional', isQuotaA: false });
      tabs.push({ label: `Quota ${k} — Nazionale`, quotaKey: k, scope: 'national', isQuotaA: false });
    }
  }
  return tabs;
};

const TIPOLOGIA_LABEL: Record<string, string> = {
  RETAIL:           'Retail',
  EXCLUSIVE:        'Exclusive',
  IMPRESE:          'Imprese',
  DIGITALE_REMOTA:  'Digitale Remota',
  TERZO_SETTORE:    'Terzo Settore',
  AGRIBUSINESS:     'Agribusiness',
  IMPACT:           'Impact',
  REMOTO:           'Remoto',
};

// Etichette sempre in MAIUSCOLO: sia quelle mappate sia le chiavi non mappate
// che arrivano grezze dal BE, così la resa è uniforme in tutta la vista.
const tipoLabel = (k: string) => (TIPOLOGIA_LABEL[k] ?? k).toUpperCase();

/* ─── Helpers ────────────────────────────────────────────── */
const fmt    = (n: number) => (n === 0 ? '—' : String(n));
const fmtPct = (n: number) => `${Math.round(n)}%`;
const numCls = 'w-14 text-right bg-white text-[#1B365D] border border-[#1B365D]/20 rounded px-1 py-0.5 text-xs focus:outline-none focus:border-[#1B365D]/50 tabular-nums';
const txtCls = 'w-full min-w-[6rem] bg-white border border-[#1B365D]/20 rounded px-1 py-0.5 text-xs text-[#1B365D] focus:outline-none focus:border-[#1B365D]/50';

/** Riga vuota Quota A da compilare (aggiunta manuale in edit). */
const emptyRowA = (): QuotaABranchRow => ({
  regional_division:        '',
  areas:                    0,
  branches:                 0,
  awarded_branches:         0,
  awarded_branches_pct:     0,
  awarded_band_i:           0,
  awarded_band_ii:          0,
  awarded_band_iii:         0,
  awarded_band_iv:          0,
  non_awarded_branches:     0,
  non_awarded_branches_pct: 0,
});

/** Riga vuota quota opzionale da compilare (aggiunta manuale in edit). */
const emptyRowOpt = (): QuotaOptionalBranchRow => ({
  regional_division:        '',
  areas:                    0,
  branches:                 0,
  awarded_branches:         0,
  awarded_branches_pct:     0,
  non_awarded_branches:     0,
  non_awarded_branches_pct: 0,
});

/** Ritorna la sezione (righe + totali) attiva. */
const sectionForTab = (
  data: Driver02Data,
  tab:  OutputTab,
  tipoKey: string,
): QuotaASection | QuotaOptionalSection | undefined => {
  if (tab.isQuotaA) {
    return data.quota_a?.[tab.scope]?.[tipoKey];
  }
  return data.optional_quotas?.[tab.quotaKey]?.[tab.scope]?.[tipoKey];
};

/** Ritorna le chiavi delle tipologie (RETAIL/EXCLUSIVE/...) per la tab corrente. */
const tipoKeysForTab = (data: Driver02Data, tab: OutputTab): string[] => {
  if (tab.isQuotaA) {
    return Object.keys(data.quota_a?.[tab.scope] ?? {});
  }
  return Object.keys(data.optional_quotas?.[tab.quotaKey]?.[tab.scope] ?? {});
};

/** Ritorna le highlights della quota corrente. */
const highlightsForTab = (data: Driver02Data, tab: OutputTab): string => {
  if (tab.isQuotaA) return data.quota_a?.highlights ?? '';
  return data.optional_quotas?.[tab.quotaKey]?.highlights ?? '';
};

/* ─── Main ───────────────────────────────────────────────── */
const FilialiAPremioContainer = ({ reportId }: { reportId: string }) => {
  const { data: remoteData, loading, error, save, isReadOnly } = useConductRiskDriver<Driver02Data>(reportId, 2);

  const [data,         setData]         = useState<Driver02Data | null>(null);
  const [savedData,    setSavedData]    = useState<Driver02Data | null>(null);
  const [isEditing,    setIsEditing]    = useState(false);
  const [activeOutput, setActiveOutput] = useState(0);
  const [activeTipo,   setActiveTipo]   = useState(0);
  const [isExporting,  setIsExporting]  = useState(false);

  useEffect(() => {
    if (remoteData) {
      setData(remoteData);
      setSavedData(remoteData);
    }
  }, [remoteData]);

  const handleOutputChange = (i: number) => { setActiveOutput(i); setActiveTipo(0); };
  const handleAnnulla = () => { if (savedData) setData(savedData); setIsEditing(false); };
  const handleSalva   = async () => {
    if (!data) return;
    await save(data);
    setSavedData(data);
    setIsEditing(false);
  };

  const handleExportXlsx = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const blob = await fetchExportConductRiskDriverXlsx(reportId, 'Driver02');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Filiali_a_Premio_${data?.period ?? 'report'}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: 'Export fallito', text: err?.message ?? 'Impossibile scaricare il file XLSX.' });
    } finally {
      setIsExporting(false);
    }
  };

  /** Aggiorna una row della Quota A (regional/national) preservando il resto. */
  const updateRowA = (
    scope:   TabScope,
    tipoKey: string,
    rowIdx:  number,
    field:   keyof QuotaABranchRow,
    raw:     string,
  ) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setData(prev => {
      if (!prev) return prev;
      const scopeSections = prev.quota_a[scope];
      const section       = scopeSections[tipoKey];
      return {
        ...prev,
        quota_a: {
          ...prev.quota_a,
          [scope]: {
            ...scopeSections,
            [tipoKey]: {
              ...section,
              rows: section.rows.map((r, i) => i !== rowIdx ? r : { ...r, [field]: val }),
            },
          },
        },
      };
    });
  };

  /** Aggiorna una row di una quota opzionale (B/C/...). */
  const updateRowOpt = (
    quotaKey: string,
    scope:    TabScope,
    tipoKey:  string,
    rowIdx:   number,
    field:    keyof QuotaOptionalBranchRow,
    raw:      string,
  ) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setData(prev => {
      if (!prev) return prev;
      const quota         = prev.optional_quotas?.[quotaKey];
      if (!quota) return prev;
      const scopeSections = quota[scope];
      const section       = scopeSections[tipoKey];
      return {
        ...prev,
        optional_quotas: {
          ...prev.optional_quotas,
          [quotaKey]: {
            ...quota,
            [scope]: {
              ...scopeSections,
              [tipoKey]: {
                ...section,
                rows: section.rows.map((r, i) => i !== rowIdx ? r : { ...r, [field]: val }),
              },
            },
          },
        },
      };
    });
  };

  /** Aggiorna il nome (Direzione) di una row della Quota A. Free-form testo. */
  const updateRowAName = (
    scope:   TabScope,
    tipoKey: string,
    rowIdx:  number,
    value:   string,
  ) => {
    setData(prev => {
      if (!prev) return prev;
      const scopeSections = prev.quota_a[scope];
      const section       = scopeSections[tipoKey];
      return {
        ...prev,
        quota_a: {
          ...prev.quota_a,
          [scope]: {
            ...scopeSections,
            [tipoKey]: {
              ...section,
              rows: section.rows.map((r, i) => i !== rowIdx ? r : { ...r, regional_division: value }),
            },
          },
        },
      };
    });
  };

  /** Aggiorna un campo numerico dei totali (tfoot) della Quota A. */
  const updateTotalsA = (
    scope:   TabScope,
    tipoKey: string,
    field:   keyof QuotaABranchRow,
    raw:     string,
  ) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setData(prev => {
      if (!prev) return prev;
      const scopeSections = prev.quota_a[scope];
      const section       = scopeSections[tipoKey];
      return {
        ...prev,
        quota_a: {
          ...prev.quota_a,
          [scope]: {
            ...scopeSections,
            [tipoKey]: { ...section, totals: { ...section.totals, [field]: val } },
          },
        },
      };
    });
  };

  /** Aggiorna il nome (Direzione) dei totali (tfoot) della Quota A. */
  const updateTotalsAName = (
    scope:   TabScope,
    tipoKey: string,
    value:   string,
  ) => {
    setData(prev => {
      if (!prev) return prev;
      const scopeSections = prev.quota_a[scope];
      const section       = scopeSections[tipoKey];
      return {
        ...prev,
        quota_a: {
          ...prev.quota_a,
          [scope]: {
            ...scopeSections,
            [tipoKey]: { ...section, totals: { ...section.totals, regional_division: value } },
          },
        },
      };
    });
  };

  /** Aggiorna il nome (Direzione) di una row di una quota opzionale. */
  const updateRowOptName = (
    quotaKey: string,
    scope:    TabScope,
    tipoKey:  string,
    rowIdx:   number,
    value:    string,
  ) => {
    setData(prev => {
      if (!prev) return prev;
      const quota         = prev.optional_quotas?.[quotaKey];
      if (!quota) return prev;
      const scopeSections = quota[scope];
      const section       = scopeSections[tipoKey];
      return {
        ...prev,
        optional_quotas: {
          ...prev.optional_quotas,
          [quotaKey]: {
            ...quota,
            [scope]: {
              ...scopeSections,
              [tipoKey]: {
                ...section,
                rows: section.rows.map((r, i) => i !== rowIdx ? r : { ...r, regional_division: value }),
              },
            },
          },
        },
      };
    });
  };

  /** Aggiorna un campo numerico dei totali (tfoot) di una quota opzionale. */
  const updateTotalsOpt = (
    quotaKey: string,
    scope:    TabScope,
    tipoKey:  string,
    field:    keyof QuotaOptionalBranchRow,
    raw:      string,
  ) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setData(prev => {
      if (!prev) return prev;
      const quota         = prev.optional_quotas?.[quotaKey];
      if (!quota) return prev;
      const scopeSections = quota[scope];
      const section       = scopeSections[tipoKey];
      return {
        ...prev,
        optional_quotas: {
          ...prev.optional_quotas,
          [quotaKey]: {
            ...quota,
            [scope]: {
              ...scopeSections,
              [tipoKey]: { ...section, totals: { ...section.totals, [field]: val } },
            },
          },
        },
      };
    });
  };

  /** Aggiorna il nome (Direzione) dei totali (tfoot) di una quota opzionale. */
  const updateTotalsOptName = (
    quotaKey: string,
    scope:    TabScope,
    tipoKey:  string,
    value:    string,
  ) => {
    setData(prev => {
      if (!prev) return prev;
      const quota         = prev.optional_quotas?.[quotaKey];
      if (!quota) return prev;
      const scopeSections = quota[scope];
      const section       = scopeSections[tipoKey];
      return {
        ...prev,
        optional_quotas: {
          ...prev.optional_quotas,
          [quotaKey]: {
            ...quota,
            [scope]: {
              ...scopeSections,
              [tipoKey]: { ...section, totals: { ...section.totals, regional_division: value } },
            },
          },
        },
      };
    });
  };

  /** Aggiunge una riga vuota alla sezione corrente (Quota A). */
  const addRowA = (scope: TabScope, tipoKey: string) => {
    setData(prev => {
      if (!prev) return prev;
      const scopeSections = prev.quota_a[scope];
      const section       = scopeSections[tipoKey];
      if (!section) return prev;
      return {
        ...prev,
        quota_a: {
          ...prev.quota_a,
          [scope]: {
            ...scopeSections,
            [tipoKey]: { ...section, rows: [...section.rows, emptyRowA()] },
          },
        },
      };
    });
  };

  /** Aggiunge una riga vuota alla sezione corrente (quota opzionale). */
  const addRowOpt = (quotaKey: string, scope: TabScope, tipoKey: string) => {
    setData(prev => {
      if (!prev) return prev;
      const quota = prev.optional_quotas?.[quotaKey];
      if (!quota) return prev;
      const scopeSections = quota[scope];
      const section       = scopeSections[tipoKey];
      if (!section) return prev;
      return {
        ...prev,
        optional_quotas: {
          ...prev.optional_quotas,
          [quotaKey]: {
            ...quota,
            [scope]: {
              ...scopeSections,
              [tipoKey]: { ...section, rows: [...section.rows, emptyRowOpt()] },
            },
          },
        },
      };
    });
  };

  const updateHighlights = (value: string) => {
    setData(prev => {
      if (!prev) return prev;
      const tab = OUTPUT_TABS[activeOutput];
      if (!tab) return prev;
      if (tab.isQuotaA) {
        return { ...prev, quota_a: { ...prev.quota_a, highlights: value } };
      }
      const quota = prev.optional_quotas?.[tab.quotaKey];
      if (!quota) return prev;
      return {
        ...prev,
        optional_quotas: {
          ...prev.optional_quotas,
          [tab.quotaKey]: { ...quota, highlights: value },
        },
      };
    });
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-12 h-12 border-[5px] border-gray-200 border-t-[#1B365D] rounded-full animate-spin" />
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>
      </div>
    );
  }
  if (!data) {
    return <div className="p-6 text-sm text-gray-400">Nessun dato disponibile per questo driver.</div>;
  }

  const OUTPUT_TABS = buildOutputTabs(data);
  const tab         = OUTPUT_TABS[activeOutput] ?? OUTPUT_TABS[0];
  const isQuotaA    = tab.isQuotaA;
  const accent      = isQuotaA ? 'text-blue-600' : 'text-violet-600';
  const tipoKeys    = tipoKeysForTab(data, tab);
  const tipoKey     = tipoKeys[activeTipo] ?? tipoKeys[0];
  const highlightsValue = highlightsForTab(data, tab);

  /* ─── KPI strips ───────────────────────────────────────── */
  const KpiStripA = ({ totals }: { totals: QuotaABranchRow }) => (
    <div className="grid grid-cols-4 gap-3">
      {[
        { label: 'Filiali',        value: totals.branches,             sub: null,                                           cls: 'text-[#1B365D]' },
        { label: 'A premio',       value: totals.awarded_branches,     sub: fmtPct(totals.awarded_branches_pct),            cls: accent           },
        { label: 'No premio',      value: totals.non_awarded_branches, sub: fmtPct(totals.non_awarded_branches_pct),        cls: 'text-gray-500'  },
        { label: 'I Fascia (top)', value: totals.awarded_band_i,       sub: totals.awarded_branches > 0 ? `${Math.round((totals.awarded_band_i / totals.awarded_branches) * 100)}% dei premiati` : null, cls: 'text-slate-600' },
      ].map(c => (
        <div key={c.label} className="bg-white rounded-xl border border-gray-100 p-4 text-center">
          <div className={`text-2xl font-bold ${c.cls}`}>{c.value}</div>
          {c.sub && <div className="text-[10px] text-gray-400 font-medium mt-0.5">{c.sub}</div>}
          <div className="text-[10px] text-gray-400 mt-1">{c.label}</div>
        </div>
      ))}
    </div>
  );

  const KpiStripOpt = ({ totals }: { totals: QuotaOptionalBranchRow }) => (
    <div className="grid grid-cols-3 gap-3">
      {[
        { label: 'Filiali',  value: totals.branches,             sub: null,                                    cls: 'text-[#1B365D]' },
        { label: 'A premio', value: totals.awarded_branches,     sub: fmtPct(totals.awarded_branches_pct),     cls: accent           },
        { label: 'No premio',value: totals.non_awarded_branches, sub: fmtPct(totals.non_awarded_branches_pct), cls: 'text-gray-500'  },
      ].map(c => (
        <div key={c.label} className="bg-white rounded-xl border border-gray-100 p-4 text-center">
          <div className={`text-2xl font-bold ${c.cls}`}>{c.value}</div>
          {c.sub && <div className="text-[10px] text-gray-400 font-medium mt-0.5">{c.sub}</div>}
          <div className="text-[10px] text-gray-400 mt-1">{c.label}</div>
        </div>
      ))}
    </div>
  );

  const section = tipoKey ? sectionForTab(data, tab, tipoKey) : undefined;

  /* ─── Render ───────────────────────────────────────────── */
  return (
    <div className="p-6 space-y-5">

      {/* Output tabs + edit bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-2 flex-wrap flex-1 min-w-0">
          {OUTPUT_TABS.map((t, i) => (
            <button
              key={`${t.quotaKey}-${t.scope}`}
              onClick={() => handleOutputChange(i)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                activeOutput === i
                  ? 'bg-[#1B365D] text-white shadow-sm'
                  : 'bg-white border border-gray-200 text-gray-500 hover:border-[#1B365D]/30 hover:text-[#1B365D]'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <button
          onClick={handleExportXlsx}
          disabled={isExporting}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-[#1B365D] border border-[#1B365D]/20 hover:bg-[#1B365D]/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Download className="w-3.5 h-3.5" />
          {isExporting ? 'Esportazione…' : 'Scarica XLSX'}
        </button>
        <EditActionBar isEditing={isEditing} onProcedi={() => setIsEditing(true)} onAnnulla={handleAnnulla} onSalva={handleSalva} disabled={isReadOnly} />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeOutput}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.18 }}
          className="space-y-4"
        >
          {/* Tipologia sub-tabs */}
          <div className="flex gap-1.5 flex-wrap">
            {tipoKeys.map((k, i) => (
              <button
                key={k}
                onClick={() => setActiveTipo(i)}
                className={`px-3 py-1 rounded-full text-[11px] font-medium transition-colors ${
                  activeTipo === i
                    ? isQuotaA ? 'bg-blue-100 text-blue-700' : 'bg-violet-100 text-violet-700'
                    : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                }`}
              >
                {tipoLabel(k)}
              </button>
            ))}
          </div>

          {/* KPI strip + table */}
          {!section ? (
            <div className="text-xs text-gray-400 p-4">Nessun dato per questa tipologia.</div>
          ) : isQuotaA ? (() => {
            const sec = section as QuotaASection;
            return (
              <>
                <KpiStripA totals={sec.totals} />
                <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                  <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                    <p className="text-xs font-semibold text-[#1B365D]">
                      {tab.label} — {tipoLabel(tipoKey)} · {data.period}
                    </p>
                    <span className="text-[10px] text-gray-400 bg-gray-50 border border-gray-100 rounded px-2 py-0.5">
                      Fasce I–IV = suddivisione interna del premio
                    </span>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-gray-50 text-gray-400 uppercase text-[10px] tracking-wide">
                          <th className="text-left px-4 py-2 font-semibold" rowSpan={2}>{tab.scope === 'regional' ? 'Direzione regionale' : 'A copertura nazionale'}</th>
                          <th className="text-right px-3 py-2 font-semibold" rowSpan={2}>Aree</th>
                          <th className="text-right px-3 py-2 font-semibold" rowSpan={2}>Filiali</th>
                          <th className="text-center px-3 py-1.5 font-semibold text-blue-500 border-l border-blue-100" colSpan={6}>Premio</th>
                          <th className="text-center px-3 py-1.5 font-semibold text-gray-400 border-l border-gray-200" colSpan={2}>No premio</th>
                        </tr>
                        <tr className="bg-gray-50 text-[10px] uppercase tracking-wide">
                          <th className="text-right px-3 py-1.5 font-semibold text-blue-600 border-l border-blue-100">Filiali</th>
                          <th className="text-right px-3 py-1.5 font-semibold text-blue-400">%</th>
                          <th className="text-right px-3 py-1.5 font-semibold text-slate-500">I Fascia</th>
                          <th className="text-right px-3 py-1.5 font-semibold text-slate-500">II Fascia</th>
                          <th className="text-right px-3 py-1.5 font-semibold text-slate-500">III Fascia</th>
                          <th className="text-right px-3 py-1.5 font-semibold text-slate-500">IV Fascia</th>
                          <th className="text-right px-3 py-1.5 font-semibold text-gray-500 border-l border-gray-200">Filiali</th>
                          <th className="text-right px-3 py-1.5 font-semibold text-gray-400">%</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {sec.rows.map((r, ri) => (
                          <motion.tr key={`${tipoKey}-${ri}`} initial={{ opacity: 0, x: -4 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: ri * 0.025 }} className="hover:bg-gray-50/60 transition-colors">
                            <td className="px-4 py-2.5 font-medium text-[#1B365D] text-[11px]">{isEditing ? <input type="text" className={txtCls} value={r.regional_division} onChange={e => updateRowAName(tab.scope, tipoKey, ri, e.target.value)} /> : r.regional_division}</td>
                            <td className="px-3 py-2 text-right text-gray-500">{isEditing ? <input type="number" className={numCls} value={r.areas} onChange={e => updateRowA(tab.scope, tipoKey, ri, 'areas', e.target.value)} /> : r.areas}</td>
                            <td className="px-3 py-2 text-right text-gray-600 font-medium">{isEditing ? <input type="number" className={numCls} value={r.branches} onChange={e => updateRowA(tab.scope, tipoKey, ri, 'branches', e.target.value)} /> : r.branches}</td>
                            <td className="px-3 py-2 text-right font-bold text-blue-600 border-l border-blue-50">{isEditing ? <input type="number" className={numCls} value={r.awarded_branches} onChange={e => updateRowA(tab.scope, tipoKey, ri, 'awarded_branches', e.target.value)} /> : r.awarded_branches}</td>
                            <td className="px-3 py-2.5 text-right text-blue-400 text-[10px]">{isEditing ? <input type="number" className={numCls} value={r.awarded_branches_pct} onChange={e => updateRowA(tab.scope, tipoKey, ri, 'awarded_branches_pct', e.target.value)} /> : fmtPct(r.awarded_branches_pct)}</td>
                            <td className="px-3 py-2 text-right text-slate-500">{isEditing ? <input type="number" className={numCls} value={r.awarded_band_i} onChange={e => updateRowA(tab.scope, tipoKey, ri, 'awarded_band_i', e.target.value)} /> : fmt(r.awarded_band_i)}</td>
                            <td className="px-3 py-2 text-right text-slate-500">{isEditing ? <input type="number" className={numCls} value={r.awarded_band_ii} onChange={e => updateRowA(tab.scope, tipoKey, ri, 'awarded_band_ii', e.target.value)} /> : fmt(r.awarded_band_ii)}</td>
                            <td className="px-3 py-2 text-right text-slate-500">{isEditing ? <input type="number" className={numCls} value={r.awarded_band_iii} onChange={e => updateRowA(tab.scope, tipoKey, ri, 'awarded_band_iii', e.target.value)} /> : fmt(r.awarded_band_iii)}</td>
                            <td className="px-3 py-2 text-right text-slate-500">{isEditing ? <input type="number" className={numCls} value={r.awarded_band_iv} onChange={e => updateRowA(tab.scope, tipoKey, ri, 'awarded_band_iv', e.target.value)} /> : fmt(r.awarded_band_iv)}</td>
                            <td className="px-3 py-2 text-right text-gray-500 border-l border-gray-100">{isEditing ? <input type="number" className={numCls} value={r.non_awarded_branches} onChange={e => updateRowA(tab.scope, tipoKey, ri, 'non_awarded_branches', e.target.value)} /> : r.non_awarded_branches}</td>
                            <td className="px-3 py-2.5 text-right text-gray-400 text-[10px]">{isEditing ? <input type="number" className={numCls} value={r.non_awarded_branches_pct} onChange={e => updateRowA(tab.scope, tipoKey, ri, 'non_awarded_branches_pct', e.target.value)} /> : fmtPct(r.non_awarded_branches_pct)}</td>
                          </motion.tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-[#1B365D] text-white text-[11px] font-semibold">
                          <td className="px-4 py-2.5">{isEditing ? <input type="text" className={txtCls} value={sec.totals.regional_division} onChange={e => updateTotalsAName(tab.scope, tipoKey, e.target.value)} /> : sec.totals.regional_division}</td>
                          <td className="px-3 py-2.5 text-right">{isEditing ? <input type="number" className={numCls} value={sec.totals.areas} onChange={e => updateTotalsA(tab.scope, tipoKey, 'areas', e.target.value)} /> : sec.totals.areas}</td>
                          <td className="px-3 py-2.5 text-right">{isEditing ? <input type="number" className={numCls} value={sec.totals.branches} onChange={e => updateTotalsA(tab.scope, tipoKey, 'branches', e.target.value)} /> : sec.totals.branches}</td>
                          <td className="px-3 py-2.5 text-right border-l border-white/10">{isEditing ? <input type="number" className={numCls} value={sec.totals.awarded_branches} onChange={e => updateTotalsA(tab.scope, tipoKey, 'awarded_branches', e.target.value)} /> : sec.totals.awarded_branches}</td>
                          <td className="px-3 py-2.5 text-right text-white/70 text-[10px]">{isEditing ? <input type="number" className={numCls} value={sec.totals.awarded_branches_pct} onChange={e => updateTotalsA(tab.scope, tipoKey, 'awarded_branches_pct', e.target.value)} /> : fmtPct(sec.totals.awarded_branches_pct)}</td>
                          <td className="px-3 py-2.5 text-right text-white/80">{isEditing ? <input type="number" className={numCls} value={sec.totals.awarded_band_i} onChange={e => updateTotalsA(tab.scope, tipoKey, 'awarded_band_i', e.target.value)} /> : fmt(sec.totals.awarded_band_i)}</td>
                          <td className="px-3 py-2.5 text-right text-white/80">{isEditing ? <input type="number" className={numCls} value={sec.totals.awarded_band_ii} onChange={e => updateTotalsA(tab.scope, tipoKey, 'awarded_band_ii', e.target.value)} /> : fmt(sec.totals.awarded_band_ii)}</td>
                          <td className="px-3 py-2.5 text-right text-white/80">{isEditing ? <input type="number" className={numCls} value={sec.totals.awarded_band_iii} onChange={e => updateTotalsA(tab.scope, tipoKey, 'awarded_band_iii', e.target.value)} /> : fmt(sec.totals.awarded_band_iii)}</td>
                          <td className="px-3 py-2.5 text-right text-white/80">{isEditing ? <input type="number" className={numCls} value={sec.totals.awarded_band_iv} onChange={e => updateTotalsA(tab.scope, tipoKey, 'awarded_band_iv', e.target.value)} /> : fmt(sec.totals.awarded_band_iv)}</td>
                          <td className="px-3 py-2.5 text-right border-l border-white/10">{isEditing ? <input type="number" className={numCls} value={sec.totals.non_awarded_branches} onChange={e => updateTotalsA(tab.scope, tipoKey, 'non_awarded_branches', e.target.value)} /> : sec.totals.non_awarded_branches}</td>
                          <td className="px-3 py-2.5 text-right text-white/70 text-[10px]">{isEditing ? <input type="number" className={numCls} value={sec.totals.non_awarded_branches_pct} onChange={e => updateTotalsA(tab.scope, tipoKey, 'non_awarded_branches_pct', e.target.value)} /> : fmtPct(sec.totals.non_awarded_branches_pct)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
                {isEditing && (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => addRowA(tab.scope, tipoKey)}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1B365D] border border-[#1B365D]/30 hover:bg-[#1B365D]/5 rounded-lg px-3 py-1.5 transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" /> Aggiungi nuovo
                    </button>
                  </div>
                )}
              </>
            );
          })() : (() => {
            const sec = section as QuotaOptionalSection;
            return (
              <>
                <KpiStripOpt totals={sec.totals} />
                <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                  <div className="px-5 py-3 border-b border-gray-100">
                    <p className="text-xs font-semibold text-[#1B365D]">
                      {tab.label} — {tipoLabel(tipoKey)} · {data.period}
                    </p>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-gray-50 text-gray-400 uppercase text-[10px] tracking-wide">
                          <th className="text-left px-4 py-2.5 font-semibold">{tab.scope === 'regional' ? 'Direzione regionale' : 'A copertura nazionale'}</th>
                          <th className="text-right px-3 py-2.5 font-semibold">Aree</th>
                          <th className="text-right px-3 py-2.5 font-semibold">Filiali</th>
                          <th className="text-right px-3 py-2.5 font-semibold text-violet-500 border-l border-violet-100">Premio Filiali</th>
                          <th className="text-right px-3 py-2.5 font-semibold text-violet-400">%</th>
                          <th className="text-right px-3 py-2.5 font-semibold text-gray-500 border-l border-gray-200">No premio Filiali</th>
                          <th className="text-right px-3 py-2.5 font-semibold text-gray-400">%</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {sec.rows.map((r, ri) => (
                          <motion.tr key={`${tipoKey}-${ri}`} initial={{ opacity: 0, x: -4 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: ri * 0.025 }} className="hover:bg-gray-50/60 transition-colors">
                            <td className="px-4 py-2.5 font-medium text-[#1B365D] text-[11px]">{isEditing ? <input type="text" className={txtCls} value={r.regional_division} onChange={e => updateRowOptName(tab.quotaKey, tab.scope, tipoKey, ri, e.target.value)} /> : r.regional_division}</td>
                            <td className="px-3 py-2 text-right text-gray-500">{isEditing ? <input type="number" className={numCls} value={r.areas} onChange={e => updateRowOpt(tab.quotaKey, tab.scope, tipoKey, ri, 'areas', e.target.value)} /> : r.areas}</td>
                            <td className="px-3 py-2 text-right text-gray-600 font-medium">{isEditing ? <input type="number" className={numCls} value={r.branches} onChange={e => updateRowOpt(tab.quotaKey, tab.scope, tipoKey, ri, 'branches', e.target.value)} /> : r.branches}</td>
                            <td className="px-3 py-2 text-right font-bold text-violet-600 border-l border-violet-50">{isEditing ? <input type="number" className={numCls} value={r.awarded_branches} onChange={e => updateRowOpt(tab.quotaKey, tab.scope, tipoKey, ri, 'awarded_branches', e.target.value)} /> : r.awarded_branches}</td>
                            <td className="px-3 py-2.5 text-right text-violet-400 text-[10px]">{isEditing ? <input type="number" className={numCls} value={r.awarded_branches_pct} onChange={e => updateRowOpt(tab.quotaKey, tab.scope, tipoKey, ri, 'awarded_branches_pct', e.target.value)} /> : fmtPct(r.awarded_branches_pct)}</td>
                            <td className="px-3 py-2 text-right text-gray-500 border-l border-gray-100">{isEditing ? <input type="number" className={numCls} value={r.non_awarded_branches} onChange={e => updateRowOpt(tab.quotaKey, tab.scope, tipoKey, ri, 'non_awarded_branches', e.target.value)} /> : r.non_awarded_branches}</td>
                            <td className="px-3 py-2.5 text-right text-gray-400 text-[10px]">{isEditing ? <input type="number" className={numCls} value={r.non_awarded_branches_pct} onChange={e => updateRowOpt(tab.quotaKey, tab.scope, tipoKey, ri, 'non_awarded_branches_pct', e.target.value)} /> : fmtPct(r.non_awarded_branches_pct)}</td>
                          </motion.tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-[#1B365D] text-white text-[11px] font-semibold">
                          <td className="px-4 py-2.5">{isEditing ? <input type="text" className={txtCls} value={sec.totals.regional_division} onChange={e => updateTotalsOptName(tab.quotaKey, tab.scope, tipoKey, e.target.value)} /> : sec.totals.regional_division}</td>
                          <td className="px-3 py-2.5 text-right">{isEditing ? <input type="number" className={numCls} value={sec.totals.areas} onChange={e => updateTotalsOpt(tab.quotaKey, tab.scope, tipoKey, 'areas', e.target.value)} /> : sec.totals.areas}</td>
                          <td className="px-3 py-2.5 text-right">{isEditing ? <input type="number" className={numCls} value={sec.totals.branches} onChange={e => updateTotalsOpt(tab.quotaKey, tab.scope, tipoKey, 'branches', e.target.value)} /> : sec.totals.branches}</td>
                          <td className="px-3 py-2.5 text-right border-l border-white/10">{isEditing ? <input type="number" className={numCls} value={sec.totals.awarded_branches} onChange={e => updateTotalsOpt(tab.quotaKey, tab.scope, tipoKey, 'awarded_branches', e.target.value)} /> : sec.totals.awarded_branches}</td>
                          <td className="px-3 py-2.5 text-right text-white/70 text-[10px]">{isEditing ? <input type="number" className={numCls} value={sec.totals.awarded_branches_pct} onChange={e => updateTotalsOpt(tab.quotaKey, tab.scope, tipoKey, 'awarded_branches_pct', e.target.value)} /> : fmtPct(sec.totals.awarded_branches_pct)}</td>
                          <td className="px-3 py-2.5 text-right border-l border-white/10">{isEditing ? <input type="number" className={numCls} value={sec.totals.non_awarded_branches} onChange={e => updateTotalsOpt(tab.quotaKey, tab.scope, tipoKey, 'non_awarded_branches', e.target.value)} /> : sec.totals.non_awarded_branches}</td>
                          <td className="px-3 py-2.5 text-right text-white/70 text-[10px]">{isEditing ? <input type="number" className={numCls} value={sec.totals.non_awarded_branches_pct} onChange={e => updateTotalsOpt(tab.quotaKey, tab.scope, tipoKey, 'non_awarded_branches_pct', e.target.value)} /> : fmtPct(sec.totals.non_awarded_branches_pct)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
                {isEditing && (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => addRowOpt(tab.quotaKey, tab.scope, tipoKey)}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1B365D] border border-[#1B365D]/30 hover:bg-[#1B365D]/5 rounded-lg px-3 py-1.5 transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" /> Aggiungi nuovo
                    </button>
                  </div>
                )}
              </>
            );
          })()}

          {/* Highlights */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <div className="flex items-center gap-2 mb-3">
              <MessageSquare className="w-3.5 h-3.5 text-[#1B365D]/60" />
              <p className="text-xs font-semibold text-[#1B365D]">
                Highlights — Quota {tab.quotaKey}
              </p>
            </div>
            {isEditing ? (
              <textarea
                className="w-full p-2 text-xs text-gray-600 leading-relaxed border border-[#1B365D]/15 rounded resize-y focus:outline-none focus:border-[#1B365D]/35 bg-gray-50/50 min-h-[120px]"
                value={highlightsValue}
                onChange={e => updateHighlights(e.target.value)}
              />
            ) : (
              <p className="text-xs text-gray-600 leading-relaxed whitespace-pre-wrap">{formatHighlights(highlightsValue)}</p>
            )}
          </div>

        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default FilialiAPremioContainer;
